import { BLOCK_COLUMN } from '@gitbook/doc-core';
import { MAX_COLUMNS } from '@gitbook/doc-core/src/columns';
import { createParagraph } from '@gitbook/doc-core/src/paragraph';
import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Block } from '@gitbook/slate';
import { useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { confirm } from '@gitbook/spine-react';
import { assert, assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import { verticalAlignments } from '../table';
import { COLUMN_DIVISIONS } from './BlockColumns';

export function useColumnBlockPaletteFactory(input: { nodeKey: string }): () => PaletteDef {
    const { nodeKey } = input;
    const editor = useEditor();
    const block = editor.value.document.assertBlock(nodeKey);
    const current = block.data.get('verticalAlignment');
    const parent = editor.value.document.getParent(block.key);
    assertIsDefined(parent, 'Column block must have a parent');
    const numberOfColumns = parent.nodes.filter((n) => n.type === BLOCK_COLUMN).size ?? 0;
    const currentIndex = parent.nodes.findIndex((n) => n.key === block.key);
    const canInsert = numberOfColumns < MAX_COLUMNS;
    const canSwapLeft = currentIndex > 0;
    const canSwapRight = currentIndex < numberOfColumns - 1;
    return () =>
        createPalette({
            position: { width: PalettePositionWidth.MEDIUM },
            groups: [
                {
                    key: 'column.alignment',
                    commands: [
                        {
                            key: 'column.vertical',
                            title: 'Vertical alignment',
                            icon: verticalAlignments.find(
                                (option) =>
                                    option.value === current ||
                                    (current === undefined && option.value === 'top')
                            )?.icon,
                            descendant: () =>
                                createPalette({
                                    groups: [
                                        {
                                            key: 'vertical-alignment',
                                            commands: verticalAlignments
                                                .map((option) =>
                                                    option.value
                                                        ? ({
                                                              key: option.value,
                                                              title: option.label,
                                                              type: PaletteCommandType.RADIO,
                                                              icon: option.icon,
                                                              selected:
                                                                  current === option.value ||
                                                                  (current === undefined &&
                                                                      option.value === 'top'),
                                                              trigger: () => {
                                                                  editor.change((change) => {
                                                                      if (option.value === 'top') {
                                                                          change.removeDataPathByKey(
                                                                              nodeKey,
                                                                              ['verticalAlignment']
                                                                          );
                                                                      } else {
                                                                          change.setDataByKey(
                                                                              nodeKey,
                                                                              ['verticalAlignment'],
                                                                              option.value
                                                                          );
                                                                      }
                                                                  });
                                                              },
                                                          } satisfies PaletteCommandDef)
                                                        : null
                                                )
                                                .filter(filterOutNullable),
                                        },
                                    ],
                                }),
                        },
                    ],
                },
                {
                    key: 'column.insert',
                    commands: [
                        canInsert
                            ? {
                                  key: 'column.insert-before',
                                  title: 'Insert column before',
                                  icon: Icon.AddColumnLeft,
                                  trigger: () => {
                                      editor.change((change) => {
                                          const parent = editor.value.document.getParent(block.key);
                                          if (!parent) {
                                              return;
                                          }
                                          const index = parent.getChildIndex(block.key);
                                          change.insertNodeByKey(
                                              parent.key,
                                              index,
                                              Block.create({
                                                  type: BLOCK_COLUMN,
                                                  nodes: [createParagraph()],
                                              })
                                          );
                                          updateColumnWidths(change, parent, block);
                                          change.normalize();
                                      });
                                  },
                              }
                            : null,
                        canInsert
                            ? {
                                  key: 'column.insert-after',
                                  title: 'Insert column after',
                                  icon: Icon.AddColumnRight,
                                  trigger: () => {
                                      editor.change((change) => {
                                          const parent = editor.value.document.getParent(block.key);
                                          if (!parent) {
                                              return;
                                          }
                                          const index = parent.getChildIndex(block.key);
                                          change.insertNodeByKey(
                                              parent.key,
                                              index + 1,
                                              Block.create({
                                                  type: BLOCK_COLUMN,
                                                  nodes: [createParagraph()],
                                              })
                                          );
                                          updateColumnWidths(change, parent, block);
                                          change.normalize();
                                      });
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable),
                },
                {
                    key: 'column.swap',
                    commands: [
                        canSwapLeft
                            ? {
                                  key: 'column.swap-left',
                                  title: 'Swap column left',
                                  icon: Icon.ArrowLeft,
                                  trigger: () => {
                                      editor.change((change) => {
                                          const parent = editor.value.document.getParent(block.key);
                                          if (!parent) {
                                              return;
                                          }
                                          const index = parent.getChildIndex(block.key);
                                          if (index === 0) {
                                              return;
                                          }
                                          const leftColumn = parent.nodes.get(index - 1);
                                          if (!leftColumn) {
                                              return;
                                          }
                                          if (!Block.isBlock(leftColumn)) {
                                              return;
                                          }
                                          // swap content of the two columns
                                          // to avoid confusion with placeholder text
                                          swapContentsOfColumns(change, block, leftColumn, {
                                              normalize: false,
                                          });
                                          swapWidthsOfColumns(change, block, leftColumn);
                                          change.normalize();
                                      });
                                  },
                              }
                            : null,
                        canSwapRight
                            ? {
                                  key: 'column.swap-right',
                                  title: 'Swap column right',
                                  icon: Icon.ArrowRight,
                                  trigger: () => {
                                      editor.change((change) => {
                                          const parent = editor.value.document.getParent(block.key);
                                          if (!parent) {
                                              return;
                                          }
                                          const index = parent.getChildIndex(block.key);
                                          if (index === parent.nodes.size - 1) {
                                              return;
                                          }
                                          const rightColumn = parent.nodes.get(index + 1);
                                          if (!rightColumn) {
                                              return;
                                          }
                                          if (!Block.isBlock(rightColumn)) {
                                              return;
                                          }
                                          // swap content of the two columns
                                          // to avoid confusion with placeholder text
                                          swapContentsOfColumns(change, block, rightColumn, {
                                              normalize: false,
                                          });
                                          swapWidthsOfColumns(change, block, rightColumn);
                                          change.normalize();
                                      });
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable),
                },
                {
                    key: 'column.danger',
                    commands: [
                        {
                            key: 'column.delete',
                            title: 'Delete column',
                            icon: Icon.Trashcan,
                            trigger: () => {
                                void confirm({
                                    title: 'Delete column',
                                    description:
                                        'Are you sure you want to delete this column and all its content?',
                                    kind: 'danger',
                                    confirmLabel: 'Delete',
                                    onConfirm: () => {
                                        editor.change((change) => {
                                            const parent = editor.value.document.getParent(
                                                block.key
                                            );
                                            if (!parent) {
                                                return;
                                            }
                                            const { width } = getColumnWidthInfo(block);
                                            const numberOfColumns = parent.nodes.size;
                                            if (numberOfColumns > 2) {
                                                const sibling =
                                                    parent.getNextSibling(block.key) ??
                                                    parent.getPreviousSibling(block.key);
                                                if (!sibling) {
                                                    return;
                                                }
                                                assert(
                                                    sibling.type === BLOCK_COLUMN,
                                                    'Sibling must be a column'
                                                );
                                                change.setDataByKey(sibling.key, ['width'], {
                                                    value:
                                                        sibling.data.get('width')?.value &&
                                                        width !== 0
                                                            ? 100 *
                                                              (sibling.data.get('width').value /
                                                                  100 +
                                                                  width)
                                                            : undefined,
                                                    unit: '%',
                                                });
                                            }
                                            change.removeNodeByKey(nodeKey);
                                            change.normalize();
                                        });
                                    },
                                });
                            },
                        },
                    ],
                },
            ],
        });
}

// After inserting a new column, adjust the widths of existing columns if necessary
function updateColumnWidths(change, parent, block) {
    // work out the available space in the columns
    const availableWidth = parent.nodes.reduce((sum, col) => {
        const { grid: colWidth } = getColumnWidthInfo(col);
        return sum - colWidth;
    }, COLUMN_DIVISIONS);
    if (availableWidth > 1) {
        return; // there's enough space to add a new column without resizing existing ones
    }

    // if block is bigger than 1 column, reduce it by 1 grid unit to make space for a new column
    const { grid: blockColWidth } = getColumnWidthInfo(block);
    if (blockColWidth > 1) {
        change.setDataByKey(block.key, ['width'], {
            value: 100 * ((blockColWidth - 1) / COLUMN_DIVISIONS),
            unit: '%',
        });
        return;
    }

    // otherwise, reduce the width of the largest column to create space for a new column
    const largestColumn = parent.nodes.reduce((largest: Block | null, col: Block) => {
        const { width: colWidthValue } = getColumnWidthInfo(col);
        if (!largest) {
            return col;
        }
        const { width: largestWidthValue } = getColumnWidthInfo(largest);
        if (colWidthValue > largestWidthValue) {
            return col;
        }
        return largest;
    }, null);
    assertIsDefined(largestColumn, 'There must be at least one column with an assigned width');

    // reduce width of largest column by 1 grid unit
    const { grid: largestColumnWidth } = getColumnWidthInfo(largestColumn);
    change.setDataByKey(largestColumn.key, ['width'], {
        value: 100 * ((largestColumnWidth - 1) / COLUMN_DIVISIONS),
        unit: '%',
    });
}

function getColumnWidthInfo(column) {
    const blockWidth = column.data.get('width');
    const width = blockWidth ? blockWidth.value / 100 : 0;
    return { blockWidth, width, grid: Math.round(width * COLUMN_DIVISIONS) };
}

function swapContentsOfColumns(
    change,
    colA: Block,
    colB: Block,
    options: { normalize?: boolean } = {}
) {
    const colAContent = colA.nodes.toArray();
    const colBContent = colB.nodes.toArray();
    const colAIsEmpty = colA.isEmpty;
    const colBIsEmpty = colB.isEmpty;
    colA.nodes.forEach((n) => change.removeNodeByKey(n.key, options));
    colB.nodes.forEach((n) => change.removeNodeByKey(n.key, options));
    if (!colAIsEmpty) {
        colAContent.reverse().forEach((n) => {
            change.insertNodeByKey(colB.key, 0, n);
        });
    }
    if (!colBIsEmpty) {
        colBContent.reverse().forEach((n) => {
            change.insertNodeByKey(colA.key, 0, n);
        });
    }
}

function swapWidthsOfColumns(change, colA: Block, colB: Block) {
    const colAWidth = colA.data.get('width');
    const colBWidth = colB.data.get('width');
    if (colAWidth) {
        change.setDataByKey(colB.key, ['width'], colAWidth);
    } else {
        change.removeDataPathByKey(colB.key, ['width']);
    }
    if (colBWidth) {
        change.setDataByKey(colA.key, ['width'], colBWidth);
    } else {
        change.removeDataPathByKey(colA.key, ['width']);
    }
}
